import { useState, useEffect } from 'react';
import { moduleAccessService, ModuleAccess, DEFAULT_MODULE_ACCESS } from '../services/moduleAccessService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook for loading and providing module access flags for the current operator.
 *
 * Combines legacy UserProfile fields (pickListActive, goodsReceptionActive, etc.)
 * with new ProfileSetting/SystemSetting values (inventoryCountingActive, etc.).
 *
 * Returns all-true defaults while loading to prevent UI flicker.
 * On error, falls back to all-true defaults so operators aren't locked out.
 *
 * Mapping to dashboard module IDs:
 *   - pickListActive → 'modern-picking'
 *   - goodsReceptionActive → 'receipt'
 *   - packingActive → 'packing'
 *   - intRelocationActive → 'put-away'
 *   - inventoryCountingActive → 'inventory'
 *   - inventoryCorrectionActive → 'correction'
 *   - warehouseTransferActive → 'movement'
 *   - locationMaintenanceActive → 'field-registration'
 */
export function useModuleAccess() {
    const { user } = useAuth();
    const [access, setAccess] = useState<ModuleAccess>(DEFAULT_MODULE_ACCESS);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) {
            setAccess(DEFAULT_MODULE_ACCESS);
            setLoading(false);
            return;
        }

        let cancelled = false;

        const load = async () => {
            try {
                setLoading(true);
                const result = await moduleAccessService.getModuleAccess();
                if (!cancelled) {
                    setAccess(result);
                }
            } catch (error) {
                console.warn('Failed to load module access:', error);
                if (!cancelled) {
                    setAccess(DEFAULT_MODULE_ACCESS);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [user?.operatorId]);

    /**
     * Check if a specific dashboard module ID is accessible for this operator.
     * Modules without a mapping are always accessible.
     */
    const isModuleAccessible = (moduleId: string): boolean => {
        switch (moduleId) {
            case 'modern-picking':
                return access.pickListActive;
            case 'receipt':
                return access.goodsReceptionActive;
            case 'packing':
                return access.packingActive;
            case 'put-away':
                return access.intRelocationActive;
            case 'inventory':
                return access.inventoryCountingActive;
            case 'correction':
                return access.inventoryCorrectionActive;
            case 'movement':
                return access.warehouseTransferActive;
            case 'field-registration':
                return access.locationMaintenanceActive;
            case 'direct-customer-order':
                return access.directOrderActive;
            default:
                // Modules without access control are always visible
                return true;
        }
    };

    return { access, loading, isModuleAccessible };
}
